import React, { useEffect, useState } from "react";
import { BrainCircuit } from "lucide-react";

interface AnimatedProgressCircleProps {
  duration?: number;
  color?: string;
  size?: number;
  iconSize?: number;
  progress?: number;
  analyzing?: boolean; // Add analyzing prop to control the animation
}

const AnimatedProgressCircle: React.FC<AnimatedProgressCircleProps> = ({
  duration = 20,
  color = "violet",
  size = 8,
  iconSize = 15,
  progress: externalProgress,
  analyzing = false, // Default to false
}) => {
  const [progress, setProgress] = useState<number>(0);

  useEffect(() => {
    if (!analyzing) {
      setProgress(0); // Reset progress when not analyzing
      return;
    }

    if (externalProgress !== undefined) {
      setProgress(externalProgress);
      return;
    }

    const intervalId = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= 100) {
          clearInterval(intervalId);
          return 100;
        }
        return prevProgress + 100 / duration;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [duration, externalProgress, analyzing]);

  return (
    <div className={`relative w-${size} h-${size}`}>
      <svg className="w-full h-full" viewBox="0 0 100 100">
        <circle
          className="text-gray-200 stroke-current"
          strokeWidth="4"
          cx="50"
          cy="50"
          r="48"
          fill="transparent"
        />
        <circle
          className={`${
            analyzing ? `text-${color}-600` : "text-gray-300"
          } progress-ring__circle stroke-current`}
          strokeWidth="5"
          strokeLinecap="round"
          cx="50"
          cy="50"
          r="48"
          fill="transparent"
          strokeDasharray="301.59"
          strokeDashoffset={301.59 - (progress / 100) * 301.59}
          style={{
            transition: "stroke-dashoffset 0.35s",
            transform: "rotate(-90deg)",
            transformOrigin: "50% 50%",
          }}
        />
      </svg>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <BrainCircuit
          size={iconSize}
          strokeWidth={2}
          className={`${analyzing ? `text-${color}-600` : "text-gray-600"}`}
        />
      </div>
    </div>
  );
};

export default AnimatedProgressCircle;
